/**
 * 检查分段是否有空隙
 * @param {Array} ranges - 分段数组
 * @param {boolean} strictMode - 严格模式（提交时检查），宽松模式（添加时检查）
 * @returns {{hasGap: boolean, message: string}}
 */
export const checkRangesGap = (ranges, strictMode = false) => {
	if (ranges.length === 0) return { hasGap: false, message: '' };

	// 排序：null值（无下限）排在最前面，然后按minMargin升序
	const sortedRanges = [...ranges].sort((a, b) => {
		if (a.minMargin === null && b.minMargin === null) return 0;
		if (a.minMargin === null) return -1;
		if (b.minMargin === null) return 1;
		return a.minMargin - b.minMargin;
	});

	// 检查第一个分段是否从 -∞ 开始
	if (sortedRanges[0].minMargin !== null) {
		return {
			hasGap: true,
			message: `分段必须从 -∞ 开始，第一个分段的最小利润率不能为空`,
		};
	}

	// 检查最后一个分段是否到 +∞ 结束（仅在严格模式下检查）
	// 注意：在添加分段的过程中（非严格模式），允许用户逐步构建分段，不强制要求最后一个分段到 +∞
	// 只有在提交表单时（严格模式），才需要检查最后一个分段是否到 +∞ 结束
	if (strictMode && sortedRanges[sortedRanges.length - 1].maxMargin !== null) {
		return {
			hasGap: true,
			message: `分段必须到 +∞ 结束，最后一个分段的最大利润率必须为空`,
		};
	}

	// 检查相邻分段是否连续
	for (let i = 0; i < sortedRanges.length - 1; i++) {
		const current = sortedRanges[i];
		const next = sortedRanges[i + 1];

		// 如果当前分段没有上限，下一个分段不应该有下限（因为当前分段已经到 +∞）
		if (current.maxMargin === null) {
			if (next.minMargin !== null) {
				return {
					hasGap: true,
					message: `分段${i + 1}到 +∞，但分段${i + 2}有下限，存在空隙`,
				};
			}
			continue;
		}

		// 如果下一个分段没有下限，当前分段应该有上限（因为下一个分段从 -∞ 开始）
		if (next.minMargin === null) {
			if (current.maxMargin !== null) {
				return {
					hasGap: true,
					message: `分段${i + 1}有上限，但分段${i + 2}从 -∞ 开始，存在空隙`,
				};
			}
			continue;
		}

		// 检查边界值是否相等
		if (current.maxMargin !== next.minMargin) {
			return {
				hasGap: true,
				message: `分段${i + 1}和分段${i + 2}之间存在空隙：分段${i + 1}的上限(${current.maxMargin}%)与分段${i + 2}的下限(${next.minMargin}%)不连续`,
			};
		}

		// 检查边界点是否被至少一个分段包含
		// 如果当前分段的maxOperator是'<'（开区间）且下一个分段的minOperator是'<'（开区间），则存在空隙
		if (current.maxOperator === '<' && next.minOperator === '<') {
			return {
				hasGap: true,
				message: `分段${i + 1}和分段${i + 2}在边界点${current.maxMargin}%处存在空隙：两个分段都不包含该边界点`,
			};
		}
	}

	return { hasGap: false, message: '' };
};

/**
 * 检查两个分段是否重叠
 * @param {Object} range1 - 分段1
 * @param {Object} range2 - 分段2
 * @returns {boolean}
 */
export const checkRangesOverlap = (range1, range2) => {
	// 获取两个分段的边界值
	const min1 = range1.minMargin === null ? -Infinity : range1.minMargin;
	const max1 = range1.maxMargin === null ? Infinity : range1.maxMargin;
	const min2 = range2.minMargin === null ? -Infinity : range2.minMargin;
	const max2 = range2.maxMargin === null ? Infinity : range2.maxMargin;

	// 如果两个分段完全相同，视为重叠
	if (min1 === min2 && max1 === max2 && range1.minOperator === range2.minOperator && range1.maxOperator === range2.maxOperator) {
		return true;
	}

	// 检查是否有重叠区间
	// 两个区间重叠的条件：它们有公共点
	// 需要考虑边界情况：当边界值相等时，需要检查运算符（开区间还是闭区间）

	// 情况1：分段1完全在分段2的左边（不重叠）
	// 如果 max1 < min2，则不重叠
	if (max1 < min2) {
		return false; // 不重叠
	}
	// 如果 max1 === min2，需要检查边界点是否同时属于两个分段
	if (max1 === min2 && range1.maxMargin !== null && range2.minMargin !== null) {
		// 分段1的结束边界等于分段2的开始边界
		// 只有当分段1包含max1（maxOperator是'≤'）且分段2包含min2（minOperator是'≤'）时，才重叠
		// 如果分段1的maxOperator是'<'（开区间），则max1不在分段1中
		// 如果分段2的minOperator是'<'（开区间），则min2不在分段2中
		// 只有当两个都包含边界点时，才重叠
		if (range1.maxOperator === '<' || range2.minOperator === '<') {
			return false; // 不重叠：至少有一个不包含边界点
		}
		// 两个都是闭区间，重叠
		return true;
	}

	// 情况2：分段2完全在分段1的左边（不重叠）
	// 如果 max2 < min1，则不重叠
	if (max2 < min1) {
		return false; // 不重叠
	}
	// 如果 max2 === min1，需要检查边界点是否同时属于两个分段
	if (max2 === min1 && range2.maxMargin !== null && range1.minMargin !== null) {
		// 分段2的结束边界等于分段1的开始边界
		// 只有当分段2包含max2（maxOperator是'≤'）且分段1包含min1（minOperator是'≤'）时，才重叠
		if (range2.maxOperator === '<' || range1.minOperator === '<') {
			return false; // 不重叠：至少有一个不包含边界点
		}
		// 两个都是闭区间，重叠
		return true;
	}

	// 情况3：两个分段有交集（重叠）
	// 如果 max1 >= min2 且 max2 >= min1，且不满足上面的不重叠条件，则重叠
	return true;
};

/**
 * 格式化利润率范围显示：10%<A≤20%
 * @param {Object} range - 分段对象
 * @returns {string}
 */
export const formatProfitMarginRange = (range) => {
	const { minMargin, minOperator, maxMargin, maxOperator } = range;
	let result = '';

	if (minMargin === null && maxMargin === null) {
		// 从 -∞ 到 +∞：显示为 A（所有值）
		result = 'A';
	} else if (minMargin === null) {
		// 从 -∞ 开始：显示为 A≤maxMargin% 或 A<maxMargin%
		// 根据 maxOperator 确定符号
		if (maxOperator === '≤') {
			result = `A≤${maxMargin}%`;
		} else {
			result = `A<${maxMargin}%`;
		}
	} else if (maxMargin === null) {
		// 到 +∞ 结束：显示为 A≥minMargin% 或 A>minMargin%
		// 根据 minOperator 确定符号（注意：minOperator='<' 表示 >，minOperator='≤' 表示 ≥）
		if (minOperator === '≤') {
			result = `A≥${minMargin}%`;
		} else {
			result = `A>${minMargin}%`;
		}
	} else {
		// 有最小值和最大值：10%<A≤20% 或 10%≤A≤20%
		result = `${minMargin}%${minOperator}A${maxOperator}${maxMargin}%`;
	}

	return result;
};
